import { useState, useCallback } from 'react';
import { useAppSelector } from '../redux/hooks';
import { validateScheduleTime, checkCartItemsForSchedule } from '../utils/scheduleValidation';

export type OrderType = 'delivery' | 'pickup';

export interface FormData {
  name: string;
  phone: string;
  email: string;
  specialRequests: string;
  address_line_1?: string;
  address_line_2?: string;
  locality?: string;
  administrative_district_level_1?: string;
  postal_code?: string;
  country?: string;
  stateCode?: string;
  isScheduled?: boolean;
  scheduledDate?: string;
  scheduledTime?: string;
  tip?: number;
}

export interface FormErrors {
  [key: string]: string | undefined;
}

interface UseCheckoutFormOptions {
  cartItems?: any[];
  onScheduleConflict?: (invalidItems: any[]) => void;
  onScheduleResolved?: () => void;
}

export const useCheckoutForm = (
  initialOrderType: OrderType = 'pickup',
  options?: UseCheckoutFormOptions
) => {
  const { selectedRestaurant } = useAppSelector(state => state.restaurant);
  const [activeTab, setActiveTab] = useState<OrderType>(initialOrderType);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    specialRequests: '',
    address_line_1: '',
    address_line_2: '',
    locality: '',
    administrative_district_level_1: '',
    postal_code: '',
    country: 'US',
    stateCode: '',
    isScheduled: false,
    scheduledDate: '',
    scheduledTime: '',
    tip: 0,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    const updatedValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const nextData = {
        ...prev,
        [name]: updatedValue,
      };

      const isPickupMode = activeTab === 'pickup';
      const isToggleTurnedOn = name === 'isScheduled' && updatedValue === true;
      const isUpdatingActiveSchedule = (name === 'scheduledDate' || name === 'scheduledTime') && nextData.isScheduled;

      if (isPickupMode && (isToggleTurnedOn || isUpdatingActiveSchedule) && options?.cartItems) {
        const targetDate = name === 'scheduledDate' ? value : nextData.scheduledDate;
        const targetTime = name === 'scheduledTime' ? value : nextData.scheduledTime;

        if (targetDate && targetTime) {
          const { invalidItems } = checkCartItemsForSchedule(options.cartItems, targetDate, targetTime);
          
          if (invalidItems.length > 0) {
            options.onScheduleConflict?.(invalidItems);
          } else {
            options.onScheduleResolved?.();
          }
        }
      }

      return nextData;
    });
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors, activeTab, options]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phone.replace(/[^\d]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (activeTab === 'delivery') {
      if (!formData.address_line_1?.trim()) newErrors.address_line_1 = 'Address is required for delivery';
      if (!formData.locality?.trim()) newErrors.locality = 'City is required for delivery';
      if (!formData.administrative_district_level_1?.trim()) newErrors.administrative_district_level_1 = 'State is required for delivery';
      if (!formData.postal_code?.trim()) newErrors.postal_code = 'ZIP code is required for delivery';
    }
    
    if (activeTab === 'pickup' && formData.isScheduled) {
      if (!formData.scheduledDate) newErrors.scheduledDate = 'Pickup date is required';
      if (!formData.scheduledTime) newErrors.scheduledTime = 'Pickup time is required';
      
      if (formData.scheduledDate && formData.scheduledTime && selectedRestaurant?.working_hours) {
        try {
          const workingHours = JSON.parse(selectedRestaurant.working_hours);
          const validation = validateScheduleTime(
            formData.scheduledDate,
            formData.scheduledTime,
            workingHours
          );
          if (!validation.isValid) {
            newErrors.scheduledTime = validation.message;
          }
        } catch (error) {
          console.error('[useCheckoutForm] Failed to parse working hours:', error);
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, activeTab, selectedRestaurant]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      specialRequests: '',
      address_line_1: '',
      address_line_2: '',
      locality: '',
      administrative_district_level_1: '',
      postal_code: '',
      country: 'US',
      stateCode: '',
      isScheduled: false,
      scheduledDate: '',
      scheduledTime: '',
      tip: 0,
    });
    setErrors({});
  }, []);

  return {
    activeTab,
    setActiveTab,
    formData,
    errors,
    handleChange,
    validateForm,
    resetForm,
  };
};